import { useState, type ReactElement } from 'react';

import {
  CFG_VAL_INPUT_FORMAT_NONE,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_FAMILY,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_SELECTABLE,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1A,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1B,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE2,
  CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE3,
  TYPE_ONLY_VALIDATION_COPY,
  defaultValidationRuleV1,
  enumValidationDraftIssues,
  isBlankValidation,
  isCfgValRuPlateFamilyStored,
  isCfgValRuPlateFormat,
  isCfgValRuPlateSelectable,
  isGrammarV1Validation,
  isLegacyPreserveValidation,
  legacyValidationPreserveNotice,
  parseOptionalFiniteNumber,
  parseOptionalNonNegInt,
  readAllowedValues,
  serializeValidationRuleV1,
  validationFamily,
} from './cfg-val-001';
import {
  RU_PLATE_FORMAT_SPECS,
  type RuPlateFormatCode,
} from './ru-plate-formats';

export type CfgValValidationEditorProps = {
  readonly valueType: string;
  readonly validation: Record<string, unknown>;
  readonly disabled?: boolean;
  readonly ariaPrefix: string;
  readonly onChange: (next: Record<string, unknown>) => void;
  /** When VEHICLE_REGISTRATION_PLATE is set, surface plate-format controls prominently. */
  readonly masterAttribute?: string | null;
};

const PLATE_POLICY_OPTIONS: readonly {
  readonly value: string;
  readonly label: string;
}[] = [
  {
    value: CFG_VAL_INPUT_FORMAT_RU_PLATE_SELECTABLE,
    label: 'Госномер РФ — тип выбирается при заполнении (рекомендуется)',
  },
  {
    value: CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1,
    label: `Фиксированно: ${RU_PLATE_FORMAT_SPECS.RU_PLATE_TYPE1.label}`,
  },
  {
    value: CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1A,
    label: `Фиксированно: ${RU_PLATE_FORMAT_SPECS.RU_PLATE_TYPE1A.label}`,
  },
  {
    value: CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE1B,
    label: `Фиксированно: ${RU_PLATE_FORMAT_SPECS.RU_PLATE_TYPE1B.label}`,
  },
  {
    value: CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE2,
    label: `Фиксированно: ${RU_PLATE_FORMAT_SPECS.RU_PLATE_TYPE2.label}`,
  },
  {
    value: CFG_VAL_INPUT_FORMAT_RU_PLATE_TYPE3,
    label: `Фиксированно: ${RU_PLATE_FORMAT_SPECS.RU_PLATE_TYPE3.label}`,
  },
  {
    value: CFG_VAL_INPUT_FORMAT_NONE,
    label: 'Другой формат / без маски',
  },
];

/**
 * Structured CFG-VAL-001 controls shared by DocumentType Admin and Template Builder.
 * Does not author checklist ITEM_VALIDATION.
 * Plate type is stored only in validation.input_format (no second competing setting).
 */
export function CfgValValidationEditor(props: CfgValValidationEditorProps): ReactElement {
  const {
    valueType,
    validation,
    disabled = false,
    ariaPrefix,
    onChange,
    masterAttribute = null,
  } = props;

  /** UI-only: user opened «Госномер РФ» but has not yet picked a concrete type. */
  const [plateFamilyPending, setPlateFamilyPending] = useState(false);

  if (isLegacyPreserveValidation(validation)) {
    return (
      <div className="stack-gap">
        <p className="meta" role="status">
          {legacyValidationPreserveNotice(validation)}
        </p>
        <p className="meta">
          Структурированное редактирование недоступно, пока правило не преобразовано в Validation
          v1.
        </p>
        <button
          type="button"
          disabled={disabled}
          aria-label={`${ariaPrefix} преобразовать в Validation v1`}
          onClick={() => {
            const confirmed = window.confirm(
              'Преобразовать в Validation v1? Историческое правило проверки будет заменено каноническим правилом v1 для текущего типа значения.',
            );
            if (confirmed) {
              onChange(defaultValidationRuleV1(valueType));
            }
          }}
        >
          Преобразовать в Validation v1
        </button>
      </div>
    );
  }

  const editable =
    isGrammarV1Validation(validation) || isBlankValidation(validation)
      ? isBlankValidation(validation)
        ? defaultValidationRuleV1(valueType)
        : validation
      : defaultValidationRuleV1(valueType);

  const family = validationFamily(valueType);

  if (family === 'string') {
    const min =
      typeof editable.min_length === 'number' && Number.isInteger(editable.min_length)
        ? String(editable.min_length)
        : '';
    const max =
      typeof editable.max_length === 'number' && Number.isInteger(editable.max_length)
        ? String(editable.max_length)
        : '';
    const storedFormat =
      typeof editable.input_format === 'string' ? editable.input_format : CFG_VAL_INPUT_FORMAT_NONE;
    const inPlateFamily = isCfgValRuPlateFamilyStored(storedFormat) || plateFamilyPending;
    const topValue = inPlateFamily
      ? CFG_VAL_INPUT_FORMAT_RU_PLATE_FAMILY
      : CFG_VAL_INPUT_FORMAT_NONE;
    const policyValue = isCfgValRuPlateFamilyStored(storedFormat) ? storedFormat : '';
    const selectedSpec = isCfgValRuPlateFormat(storedFormat)
      ? RU_PLATE_FORMAT_SPECS[storedFormat as RuPlateFormatCode]
      : null;
    const showPlateEmphasis = masterAttribute === 'VEHICLE_REGISTRATION_PLATE';

    const writeFormat = (nextFormat: string | null) => {
      const next = { ...editable };
      if (nextFormat === null || nextFormat === CFG_VAL_INPUT_FORMAT_NONE) {
        delete next.input_format;
      } else {
        next.input_format = nextFormat;
      }
      onChange(serializeValidationRuleV1(valueType, next));
    };

    return (
      <div className="stack-gap">
        {showPlateEmphasis ? (
          <p className="meta" role="status" data-testid="cfg-val-plate-master-hint">
            Для VEHICLE_REGISTRATION_PLATE рекомендуется «Госномер РФ — тип выбирается при
            заполнении». Выбор формата не создаёт master-привязку и не привязывает номер цистерны к
            PRIMARY_VEHICLE автоматически.
          </p>
        ) : null}
        <label>
          Формат ввода
          <select
            aria-label={`${ariaPrefix} формат ввода`}
            data-testid="cfg-val-input-format"
            disabled={disabled}
            value={topValue}
            onChange={(event) => {
              const selected = event.target.value;
              if (selected === CFG_VAL_INPUT_FORMAT_NONE) {
                setPlateFamilyPending(false);
                writeFormat(null);
                return;
              }
              // One-step (PO 02C): deliberate «Госномер РФ» with no prior plate format
              // persists SELECTABLE immediately — no compulsory second «Политика» click.
              // Existing TYPE*/SELECTABLE is never rewritten merely by opening the family UI.
              if (!isCfgValRuPlateFamilyStored(storedFormat)) {
                setPlateFamilyPending(false);
                writeFormat(CFG_VAL_INPUT_FORMAT_RU_PLATE_SELECTABLE);
                return;
              }
              setPlateFamilyPending(false);
            }}
          >
            <option value={CFG_VAL_INPUT_FORMAT_NONE}>Обычный текст</option>
            <option value={CFG_VAL_INPUT_FORMAT_RU_PLATE_FAMILY}>Госномер РФ</option>
          </select>
        </label>
        {showPlateEmphasis && !inPlateFamily ? (
          <p className="meta" role="status" data-testid="cfg-val-plate-ordinary-text-warn">
            Поле с привязкой VEHICLE_REGISTRATION_PLATE сейчас как обычный текст — маски и выбор
            типа при заполнении не активны. Master-привязка сама по себе формат не меняет.
          </p>
        ) : null}
        {showPlateEmphasis && !inPlateFamily ? (
          <button
            type="button"
            disabled={disabled}
            data-testid="cfg-val-plate-one-step-selectable"
            aria-label={`${ariaPrefix} включить госномер РФ с выбором типа при заполнении`}
            onClick={() => {
              setPlateFamilyPending(false);
              writeFormat(CFG_VAL_INPUT_FORMAT_RU_PLATE_SELECTABLE);
            }}
          >
            Госномер РФ — выбор типа при заполнении
          </button>
        ) : null}
        {topValue === CFG_VAL_INPUT_FORMAT_RU_PLATE_FAMILY ? (
          <label>
            Политика типа (расширенно: фиксированный TYPE* для legacy)
            <select
              aria-label={`${ariaPrefix} тип регистрационного знака`}
              data-testid="cfg-val-plate-type"
              disabled={disabled}
              value={policyValue || CFG_VAL_INPUT_FORMAT_RU_PLATE_SELECTABLE}
              onChange={(event) => {
                const selected = event.target.value;
                setPlateFamilyPending(false);
                if (selected === '' || selected === CFG_VAL_INPUT_FORMAT_NONE) {
                  writeFormat(CFG_VAL_INPUT_FORMAT_RU_PLATE_SELECTABLE);
                  return;
                }
                writeFormat(selected);
              }}
            >
              {PLATE_POLICY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {isCfgValRuPlateSelectable(storedFormat) ? (
          <p className="meta" data-testid="cfg-val-plate-type-hint">
            При заполнении заявки подрядчик сам выберет тип знака (1 / 1А / 1Б / 2 / 3 или иной). Не
            выводится из вида техники. Один шаблон допускает разные типы на разных заявках и полях.
          </p>
        ) : selectedSpec !== null ? (
          <p className="meta" data-testid="cfg-val-plate-type-hint">
            Фиксированный тип для всех заявок по этому полю. Пример: {selectedSpec.example}.{' '}
            {selectedSpec.hint}
          </p>
        ) : (
          <p className="meta">
            Формат задаётся вручную; не выводится из подписи поля. Совместим с
            VEHICLE_REGISTRATION_PLATE, но не требует её. Хранится только в validation.input_format.
          </p>
        )}
        <label>
          Минимальная длина
          <input
            aria-label={`${ariaPrefix} минимальная длина`}
            inputMode="numeric"
            disabled={disabled}
            value={min}
            onChange={(event) => {
              const next = { ...editable };
              const parsed = parseOptionalNonNegInt(event.target.value);
              if (parsed === undefined) {
                delete next.min_length;
              } else {
                next.min_length = parsed;
              }
              onChange(serializeValidationRuleV1(valueType, next));
            }}
          />
        </label>
        <label>
          Максимальная длина
          <input
            aria-label={`${ariaPrefix} максимальная длина`}
            inputMode="numeric"
            disabled={disabled}
            value={max}
            onChange={(event) => {
              const next = { ...editable };
              const parsed = parseOptionalNonNegInt(event.target.value);
              if (parsed === undefined) {
                delete next.max_length;
              } else {
                next.max_length = parsed;
              }
              onChange(serializeValidationRuleV1(valueType, next));
            }}
          />
        </label>
      </div>
    );
  }

  if (family === 'number') {
    const minimum =
      typeof editable.minimum === 'number' && Number.isFinite(editable.minimum)
        ? String(editable.minimum)
        : '';
    const maximum =
      typeof editable.maximum === 'number' && Number.isFinite(editable.maximum)
        ? String(editable.maximum)
        : '';
    return (
      <div className="stack-gap">
        <label>
          Минимальное значение
          <input
            aria-label={`${ariaPrefix} минимальное значение`}
            inputMode="decimal"
            disabled={disabled}
            value={minimum}
            onChange={(event) => {
              const next = { ...editable };
              const parsed = parseOptionalFiniteNumber(event.target.value);
              if (parsed === undefined) {
                delete next.minimum;
              } else {
                next.minimum = parsed;
              }
              onChange(serializeValidationRuleV1(valueType, next));
            }}
          />
        </label>
        <label>
          Максимальное значение
          <input
            aria-label={`${ariaPrefix} максимальное значение`}
            inputMode="decimal"
            disabled={disabled}
            value={maximum}
            onChange={(event) => {
              const next = { ...editable };
              const parsed = parseOptionalFiniteNumber(event.target.value);
              if (parsed === undefined) {
                delete next.maximum;
              } else {
                next.maximum = parsed;
              }
              onChange(serializeValidationRuleV1(valueType, next));
            }}
          />
        </label>
      </div>
    );
  }

  if (family === 'enum') {
    const values = readAllowedValues(editable);
    const rows = values.length > 0 ? values : [''];
    const draftIssues = enumValidationDraftIssues({
      version: 1,
      allowed_values: rows,
    });
    return (
      <div className="stack-gap">
        <p className="meta">Разрешённые значения</p>
        {draftIssues.map((message) => (
          <p key={message} className="meta" role="alert">
            {message}
          </p>
        ))}
        {rows.map((value, index) => (
          <div key={`enum-${index}`} className="actions">
            <label>
              Значение {index + 1}
              <input
                aria-label={`${ariaPrefix} разрешённое значение ${index + 1}`}
                disabled={disabled}
                value={value}
                onChange={(event) => {
                  const nextValues = [...rows];
                  nextValues[index] = event.target.value;
                  onChange(
                    serializeValidationRuleV1(valueType, {
                      ...editable,
                      allowed_values: nextValues,
                    }),
                  );
                }}
              />
            </label>
            <button
              type="button"
              disabled={disabled || rows.length <= 1}
              aria-label={`${ariaPrefix} удалить значение ${index + 1}`}
              onClick={() => {
                const nextValues = rows.filter((_, rowIndex) => rowIndex !== index);
                onChange(
                  serializeValidationRuleV1(valueType, {
                    ...editable,
                    allowed_values: nextValues.length > 0 ? nextValues : [''],
                  }),
                );
              }}
            >
              Удалить
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={disabled}
          aria-label={`${ariaPrefix} добавить разрешённое значение`}
          onClick={() => {
            onChange(
              serializeValidationRuleV1(valueType, {
                ...editable,
                allowed_values: [...rows, ''],
              }),
            );
          }}
        >
          Добавить значение
        </button>
      </div>
    );
  }

  if (family === 'type_only') {
    return (
      <p className="meta" role="status">
        {TYPE_ONLY_VALIDATION_COPY}
      </p>
    );
  }

  return (
    <p className="meta" role="status">
      Тип значения не поддерживает структурированную Validation v1 в этой форме.
    </p>
  );
}
